import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TbArrowRight, TbCreditCard, TbHeart, TbHeartBroken } from 'react-icons/tb';
import { BiInfoCircle } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';
import { SiMastercard, SiPaypal, SiSepa } from 'react-icons/si';
import { FaCcAmex, FaCcVisa } from 'react-icons/fa6';
import Button from '../../components/buttons/Button';
import useActiveSubscription from '../../../hooks/business/useActiveSubscription';
import {
  baseSubscriptionPrices,
  variableSubscriptionPrices,
} from '../../../utils/subscriptionUtils';
import { InvoiceData, PaymentDetails } from '../../../types/payments';
import InvoicesTable from '../../components/tables/InvoicesTable';
import useSelectedBusiness from '../../../hooks/business/useSelectedBusiness';
import businessService from '../../../services/businessService';
import { subscriptionService } from '../../../services/subscriptionService';
import PageHeader from '../../components/misc/PageHeader';
import TrialBanner from '../../components/misc/TrialBanner';
import { formatDate } from '../../../utils/stringUtils';

export default function ManageSubscriptionPage(): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.settings.manageSubscription' });
  const { t: subscriptionT } = useTranslation('', { keyPrefix: 'page.settings.subscriptions' });
  const activeSubscription = useActiveSubscription();
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>();
  const [lastInvoices, setLastInvoices] = useState<InvoiceData[]>();
  const [userCount, setUserCount] = useState(0);
  const [endDate, setEndDate] = useState<Date | null>();
  const [isTrialActive, setIsTrialActive] = useState(false);
  const selectedBusiness = useSelectedBusiness();
  const navigate = useNavigate();

  useEffect(() => {
    businessService.fetchBusinesses();
  }, []);

  useEffect(() => {
    const trialActive =
      selectedBusiness?.trialEndDate && new Date(selectedBusiness.trialEndDate) > new Date();
    setIsTrialActive(!!trialActive);
  }, [selectedBusiness]);

  useEffect(() => {
    const fetchData = async () => {
      setPaymentDetails(undefined);
      setLastInvoices([]);
      setUserCount(0);

      try {
        const details = await subscriptionService.getPaymentDetails();
        setPaymentDetails(details);
        if (details) {
          const invoices = await subscriptionService.getLastInvoices(3);
          setLastInvoices(invoices);
          const count = await subscriptionService.getSubscriptionUsage();
          setUserCount(count);
          const x = await subscriptionService.getEndDate();
          setEndDate(new Date(x * 1000));
        }
      } catch {
        setPaymentDetails(null);
      }
    };

    fetchData();
  }, [activeSubscription, selectedBusiness]);

  function shouldShowTrialBanner(): boolean {
    if (
      !selectedBusiness?.trialEndDate ||
      !selectedBusiness?.trialType ||
      selectedBusiness?.subscription?.subscriptionType
    )
      return false;
    const now = new Date();
    const trialEnd = new Date(selectedBusiness.trialEndDate);
    const daysLeft = Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return trialEnd.getTime() > now.getTime() && daysLeft < 31;
  }

  let calculatedAmount = baseSubscriptionPrices[activeSubscription] || 0;
  calculatedAmount +=
    userCount > 25 ? variableSubscriptionPrices[activeSubscription] * (userCount - 25) : 0;

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        badge={
          <span className="rounded-xl border-2 border-primary-900 px-4 py-0.5 text-lg font-semibold uppercase">
            {subscriptionT(`${activeSubscription.toLowerCase()}.title`)}
          </span>
        }
      />

      {selectedBusiness &&
        selectedBusiness.trialEndDate &&
        selectedBusiness.trialType &&
        shouldShowTrialBanner() && (
          <TrialBanner
            trialEndDate={new Date(selectedBusiness.trialEndDate)}
            plan={selectedBusiness.trialType}
            onPaymentSetup={() =>
              navigate('/subscriptions/upgrade', {
                state: { changePaymentMode: false, subscriptionType: activeSubscription },
              })
            }
          />
        )}

      <div className="mb-6 rounded-xl bg-secondary-200 bg-opacity-50 p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-primary-900">{t('yourSubscription')}</h3>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <h2 className="text-2xl font-bold text-primary-900">
                {subscriptionT(`${activeSubscription.toLowerCase()}.title`)}{' '}
                {isTrialActive ? `(${t('freeTrial')})` : ''}
                {selectedBusiness?.subscription?.willRenew === false && (
                  <>
                    <TbArrowRight className="mx-2 inline" />
                    {subscriptionT(`free.title`)}
                  </>
                )}
              </h2>
            </div>
            {selectedBusiness?.subscription?.willRenew === true && (
              <div className="mt-2 flex flex-col gap-1 rounded-md border border-primary-300 px-2 py-1 text-base font-bold text-primary-900">
                <div className="flex items-center gap-2">
                  <BiInfoCircle className="mr-1 inline" />
                  {t('nextPaymentText', { amount: calculatedAmount.toFixed(2) })}
                </div>
                <div className="text-primary-700 ml-7 flex flex-col gap-1 text-sm font-normal">
                  {userCount > 25 && (
                    <>
                      <span>
                        {t('breakdown.base', {
                          amount: (baseSubscriptionPrices[activeSubscription] || 0).toFixed(2),
                        })}
                      </span>
                      <span>
                        {t('breakdown.members', {
                          count: userCount - 25,
                          price: (
                            variableSubscriptionPrices[activeSubscription] *
                            (userCount - 25)
                          ).toFixed(2),
                        })}
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}
            {selectedBusiness?.subscription?.willRenew === false && endDate && (
              <div className="col-span-3 mt-2 rounded-md border border-secondary px-2 py-1 text-base font-bold text-secondary">
                {t('cancelledText', { date: formatDate(endDate) })}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-3 lg:items-end">
            <Button variant="primary" onClick={() => navigate('/settings/subscriptions')}>
              {t('changeSubscription')}
            </Button>
            <div className="flex flex-col justify-end gap-3 sm:flex-row">
              {selectedBusiness?.subscription?.willRenew === true && (
                <Button
                  onClick={() => {
                    subscriptionService.cancelSubscription().then(() => {
                      businessService.fetchBusinesses();
                      subscriptionService
                        .getEndDate()
                        .then((x: number) => setEndDate(new Date(x * 1000)))
                        .catch(() => setEndDate(null));
                    });
                  }}
                  variant="danger"
                  className="w-full sm:w-auto">
                  <TbHeartBroken className="h-4 w-4" />
                  {t('cancel')}
                </Button>
              )}
              {selectedBusiness?.subscription?.willRenew === false && (
                <Button
                  onClick={() => {
                    subscriptionService.reactivateSubscription().then(() => {
                      businessService.fetchBusinesses();
                    });
                  }}
                  variant="danger"
                  className="w-full sm:w-auto">
                  <TbHeart className="h-4 w-4" />
                  {t('reactivate')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mb-6 rounded-xl bg-secondary-200 bg-opacity-50 p-6">
        <h3 className="mb-4 text-lg font-semibold text-primary-900">Recent Invoices</h3>
        <InvoicesTable invoices={lastInvoices || []} loading={lastInvoices === undefined} />
        <div className="mt-4 flex justify-end">
          <Button
            onClick={() => navigate('/subscriptions/invoices')}
            variant="primary"
            className="px-6">
            {t('showAllInvoices')}
          </Button>
        </div>
      </div>

      <div className="mb-6 rounded-xl bg-secondary-200 bg-opacity-50 p-6">
        <h3 className="mb-2 text-lg font-semibold text-primary-900">{t('paymentMethod')}</h3>
        {isTrialActive && selectedBusiness?.subscription?.willRenew === false ? (
          <div className="mb-4 rounded-lg bg-secondary p-4">
            <p className="text-sm font-medium text-secondary-50">{t('trialPaymentRequired')}</p>
            <p className="mt-1 text-sm text-secondary-50">{t('trialPaymentHelper')}</p>
          </div>
        ) : (
          <p className="mb-4 text-sm text-gray-500">{t('paymentMethodHelper')}</p>
        )}
        {paymentDetails === undefined ? (
          <div className="h-16 w-full animate-pulse rounded-xl bg-secondary-200" />
        ) : (
          <PaymentDetailsRow paymentDetails={paymentDetails} />
        )}
      </div>
    </>
  );
}

type PaymentDetailsRowProps = {
  paymentDetails: PaymentDetails | null;
};

function PaymentDetailsRow({ paymentDetails }: PaymentDetailsRowProps) {
  const { t } = useTranslation('', { keyPrefix: 'page.settings.manageSubscription' });
  const navigate = useNavigate();
  const activeSubscription = useActiveSubscription();
  const selectedBusiness = useSelectedBusiness();
  const isTrialActive =
    selectedBusiness?.trialEndDate && new Date(selectedBusiness.trialEndDate) > new Date();

  return (
    <div className="flex items-center justify-between rounded-xl p-4">
      <div className="flex items-center gap-3">
        {paymentDetails ? (
          paymentDetails.type === 'card' ? (
            <>
              <div className="flex h-10 w-10 items-center justify-center">
                {paymentDetails.brand === 'visa' ? (
                  <FaCcVisa size={32} className="text-primary-300" />
                ) : paymentDetails.brand === 'mastercard' || paymentDetails.brand === 'maestro' ? (
                  <SiMastercard size={28} className="text-danger" />
                ) : paymentDetails.brand === 'amex' ? (
                  <FaCcAmex size={28} className="text-primary-300" />
                ) : (
                  <TbCreditCard size={28} className="text-gray-600" />
                )}
              </div>
              <div>
                <p className="font-medium text-primary-900">
                  {t('card')} {t('endingIn')} {paymentDetails.last4}
                </p>
                <p className="text-sm text-gray-500">
                  {t('expiryDate')}: {paymentDetails.exp_month}/{paymentDetails.exp_year}
                </p>
              </div>
            </>
          ) : paymentDetails.type === 'sepa_debit' ? (
            <>
              <div className="flex h-10 w-10 items-center justify-center">
                <SiSepa size={28} className="text-primary-300" />
              </div>
              <div>
                <p className="font-medium text-primary-900">
                  {paymentDetails.name} - {paymentDetails.bank_code} {t('endingIn')}{' '}
                  {paymentDetails.last4}
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="flex h-10 w-10 items-center justify-center">
                <SiPaypal size={28} className="text-primary-300" />
              </div>
              <div>
                <p className="font-medium text-primary-900">
                  {paymentDetails.name && `${paymentDetails.name}, `}
                  {paymentDetails.email}
                </p>
              </div>
            </>
          )
        ) : (
          <>
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-300 bg-opacity-20">
              <TbCreditCard size={20} className="text-primary-900" />
            </div>
            <p className="font-medium text-primary-900">
              {isTrialActive ? t('setupPaymentMethodForTrial') : t('noPaymentMethod')}
            </p>
          </>
        )}
      </div>
      <Button
        disabled={!selectedBusiness?.subscription && !selectedBusiness?.trialEndDate}
        onClick={
          isTrialActive && !paymentDetails
            ? () =>
                navigate('/subscriptions/upgrade', {
                  state: { changePaymentMode: false, subscriptionType: activeSubscription },
                })
            : () =>
                navigate('/subscriptions/upgrade', {
                  state: { changePaymentMode: true, subscriptionType: activeSubscription },
                })
        }
        variant="primary">
        {isTrialActive && !paymentDetails ? t('setupPaymentMethod') : t('editPaymentMethod')}
      </Button>
    </div>
  );
}
